import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Select,
  Switch,
  Table,
  Tabs,
  Text,
} from '@radix-ui/themes';
import { DownloadIcon, ReloadIcon } from '@radix-ui/react-icons';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import AdminLayout from './AdminLayout';
import { adminApi } from '../../services/api';
import { useOrganization } from '../../contexts/OrganizationContext';

type ReportTab = 'dashboard' | 'kpi1' | 'kpi2' | 'kpi3';

const todayIso = (): string => new Date().toISOString().slice(0, 10);

const mondayOfWeek = (d: Date): string => {
  const copy = new Date(d);
  const day = copy.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + diff);
  return copy.toISOString().slice(0, 10);
};

const firstOfMonth = (d: Date): string => {
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

interface AdoptionExports {
  csv: string;
  xlsx_base64: string;
  xlsx_filename?: string;
  workbook_xlsx_base64?: string;
  workbook_xlsx_filename?: string;
}

interface AdoptionResponse {
  columns: string[];
  column_labels?: Record<string, string>;
  records: Record<string, unknown>[];
  meta?: Record<string, unknown>;
  exports?: AdoptionExports;
}

// Rendered when the backend emits null for a field the org has no data source
// for (e.g. SMC eligibility) — PHA-566 feedback #4.2.
const NA_TEXT = 'Data is not available.';

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const base64ToBlob = (b64: string, mime: string): Blob => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
};

const TAB_CONFIG: { value: ReportTab; label: string; periodType: 'month' | 'week' }[] = [
  { value: 'dashboard', label: 'Dashboard', periodType: 'month' },
  { value: 'kpi1', label: 'KPI 1 — Billable (MOM)', periodType: 'month' },
  { value: 'kpi2', label: 'KPI 2 — Enrollment (WOW)', periodType: 'week' },
  { value: 'kpi3', label: 'KPI 3 — Usage (WOW)', periodType: 'week' },
];

const formatColumnHeader = (key: string): string =>
  key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Count metrics per tab to plot over time when trend history is on. Summed
// across clients per period, so only additive (count) metrics belong here.
const CHART_SERIES: Record<ReportTab, { key: string; label: string; color: string }[]> = {
  dashboard: [
    { key: 'total_billable', label: 'Billable', color: '#2563eb' },
    { key: 'total_active_patients', label: 'Active', color: '#16a34a' },
    { key: 'patients_missed_billing', label: 'Missed Billing', color: '#dc2626' },
  ],
  kpi1: [
    // PHA-566 bug-list-2 #1: repointed from the removed duplicate `total_onboarded`.
    { key: 'total_patients_onboarded', label: 'Onboarded', color: '#64748b' },
    { key: 'total_billable', label: 'Billable', color: '#2563eb' },
    { key: 'total_inactive_patients', label: 'Inactive', color: '#dc2626' },
  ],
  kpi2: [
    { key: 'eligible_visited', label: 'Eligible Visited', color: '#2563eb' },
    { key: 'enrolled_last_week', label: 'Weekly Enrollment', color: '#16a34a' },
  ],
  kpi3: [
    { key: 'total_active_this_month', label: 'Active This Month', color: '#16a34a' },
    { key: 'inactive_patients', label: 'Inactive', color: '#dc2626' },
    { key: 'total_patients_with_rpm_device', label: 'RPM Device', color: '#7c3aed' },
  ],
};

const periodKeyForColumns = (columns: string[]): string => {
  if (columns.includes('month') && !columns.includes('week')) return 'month';
  if (columns.includes('week')) return 'week';
  return 'period_label';
};

// Sum the additive series across clients within each period, sorted ascending.
const buildTrendData = (
  records: Record<string, unknown>[],
  periodKey: string,
  seriesKeys: string[]
): Record<string, number | string>[] => {
  const byPeriod = new Map<string, Record<string, number | string>>();
  records.forEach((row) => {
    const period = row[periodKey];
    if (period === null || period === undefined) return;
    const p = String(period);
    if (!byPeriod.has(p)) {
      const seed: Record<string, number | string> = { period: p };
      seriesKeys.forEach((k) => (seed[k] = 0));
      byPeriod.set(p, seed);
    }
    const bucket = byPeriod.get(p)!;
    seriesKeys.forEach((k) => {
      const v = row[k];
      if (typeof v === 'number') bucket[k] = (bucket[k] as number) + v;
    });
  });
  return Array.from(byPeriod.values()).sort((a, b) =>
    String(a.period).localeCompare(String(b.period))
  );
};

const AdminAdoptionTrackingPage: React.FC = () => {
  const { orgId } = useOrganization();
  const [tab, setTab] = useState<ReportTab>('dashboard');
  const [periodStart, setPeriodStart] = useState(firstOfMonth(new Date()));
  const [periodMode, setPeriodMode] = useState<'full_month' | 'mid_month'>('full_month');
  const [showHistory, setShowHistory] = useState(true);
  const [fromPeriod, setFromPeriod] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AdoptionResponse | null>(null);

  const activeConfig = TAB_CONFIG.find((t) => t.value === tab) ?? TAB_CONFIG[0];
  const organizationId = orgId && orgId !== 'ALL' ? orgId : 'ALL';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminApi.getAdoptionTracking({
        report: tab,
        period_type: activeConfig.periodType,
        period_start: periodStart,
        organization_id: organizationId,
        period_mode: tab === 'kpi1' ? periodMode : undefined,
        history: showHistory,
        from_period: showHistory && fromPeriod ? fromPeriod : undefined,
      });
      setData(result as AdoptionResponse);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load report');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [tab, activeConfig.periodType, periodStart, organizationId, periodMode, showHistory, fromPeriod]);

  useEffect(() => {
    load();
  }, [load]);

  const handleTabChange = (value: string) => {
    const next = value as ReportTab;
    setTab(next);
    const cfg = TAB_CONFIG.find((t) => t.value === next);
    if (cfg?.periodType === 'week') {
      setPeriodStart(mondayOfWeek(new Date()));
    } else {
      setPeriodStart(firstOfMonth(new Date()));
    }
  };

  const downloadCsv = () => {
    const csv = data?.exports?.csv;
    if (!csv) {
      setError('CSV export is not available for this report load.');
      return;
    }
    downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `adoption-${tab}.csv`);
  };

  const downloadXlsx = async (allTabs: boolean) => {
    const fromCache = allTabs
      ? data?.exports?.workbook_xlsx_base64
      : data?.exports?.xlsx_base64;
    const cachedName = allTabs
      ? data?.exports?.workbook_xlsx_filename
      : data?.exports?.xlsx_filename;
    if (fromCache) {
      downloadBlob(
        base64ToBlob(
          fromCache,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ),
        cachedName || (allTabs ? 'adoption-tracking.xlsx' : `adoption-${tab}.xlsx`)
      );
      return;
    }
    if (!allTabs) {
      setError('Excel export is not available for this report load.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = (await adminApi.getAdoptionTracking({
        report: tab,
        period_type: activeConfig.periodType,
        period_start: periodStart,
        organization_id: organizationId,
        period_mode: tab === 'kpi1' ? periodMode : undefined,
        include_all_tabs: true,
      })) as AdoptionResponse;
      setData(result);
      const b64 = result.exports?.workbook_xlsx_base64;
      if (!b64) {
        setError('Workbook export is not available for this period.');
        return;
      }
      downloadBlob(
        base64ToBlob(b64, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'),
        result.exports?.workbook_xlsx_filename || 'adoption-tracking.xlsx'
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setLoading(false);
    }
  };

  const columns = data?.columns ?? [];
  const columnLabels = data?.column_labels ?? {};
  const records = data?.records ?? [];

  const headerFor = (col: string): string => {
    if (col === 'client') return 'CLIENT';
    return columnLabels[col] || formatColumnHeader(col);
  };

  const renderCell = (value: unknown): string => {
    if (value === null || value === undefined) return NA_TEXT;
    return String(value);
  };

  const periodLabel = useMemo(() => {
    if (data?.meta?.period_label) return String(data.meta.period_label);
    return '';
  }, [data]);

  const chartSeries = CHART_SERIES[tab];
  const trendData = useMemo(() => {
    if (!showHistory || records.length === 0) return [];
    const periodKey = periodKeyForColumns(columns);
    return buildTrendData(records, periodKey, chartSeries.map((s) => s.key));
  }, [records, columns, showHistory, chartSeries]);

  return (
    <AdminLayout
      title="Adoption Tracking"
      subtitle="Clinic and patient adoption KPIs. Metrics align with billing report and Smart Hub definitions."
    >
      <Card className="mb-4 p-4">
        <Flex gap="3" wrap="wrap" align="end">
          <Box>
            <Text size="1" color="gray" mb="1" as="div">
              {activeConfig.periodType === 'week' ? 'Week starting (Monday)' : 'Month'}
            </Text>
            <input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              className="border border-slate-6 rounded px-2 py-1 text-sm"
            />
          </Box>
          {tab === 'kpi1' && (
            <Box>
              <Text size="1" color="gray" mb="1" as="div">
                Month mode
              </Text>
              <Select.Root
                value={periodMode}
                onValueChange={(v) => setPeriodMode(v as 'full_month' | 'mid_month')}
              >
                <Select.Trigger />
                <Select.Content>
                  <Select.Item value="full_month">Full month</Select.Item>
                  <Select.Item value="mid_month">Mid-month running total</Select.Item>
                </Select.Content>
              </Select.Root>
            </Box>
          )}
          <Box>
            <Text size="1" color="gray" mb="1" as="div">
              Trend history
            </Text>
            <Flex align="center" gap="2" style={{ height: 32 }}>
              <Switch checked={showHistory} onCheckedChange={setShowHistory} />
              <Text size="1" color="gray">
                {showHistory ? 'On (all periods)' : 'Off (single period)'}
              </Text>
            </Flex>
          </Box>
          {showHistory && (
            <Box>
              <Text size="1" color="gray" mb="1" as="div">
                From (optional)
              </Text>
              <input
                type="date"
                value={fromPeriod}
                onChange={(e) => setFromPeriod(e.target.value)}
                className="border border-slate-6 rounded px-2 py-1 text-sm"
              />
            </Box>
          )}
          <Button variant="soft" onClick={load} disabled={loading}>
            <ReloadIcon />
            Refresh
          </Button>
          <Button variant="outline" onClick={downloadCsv} disabled={loading || !data?.exports?.csv}>
            <DownloadIcon />
            Export CSV
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadXlsx(false)}
            disabled={loading || !data?.exports?.xlsx_base64}
          >
            <DownloadIcon />
            Export Excel (tab)
          </Button>
          <Button variant="outline" onClick={() => downloadXlsx(true)} disabled={loading}>
            <DownloadIcon />
            Export Excel (all tabs)
          </Button>
        </Flex>
        {periodLabel && (
          <Text size="2" color="gray" mt="2">
            Period: {periodLabel}
          </Text>
        )}
      </Card>

      <Tabs.Root value={tab} onValueChange={handleTabChange}>
        <Tabs.List>
          {TAB_CONFIG.map((t) => (
            <Tabs.Trigger key={t.value} value={t.value}>
              {t.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
      </Tabs.Root>

      {error && (
        <Text color="red" size="2" mt="3">
          {error}
        </Text>
      )}

      {showHistory && trendData.length >= 2 && (
        <Card className="mt-4 p-4">
          <Heading size="3" mb="1">
            Trend over time
          </Heading>
          <Text size="1" color="gray" mb="3" as="div">
            Summed across clients per period. {trendData.length} periods.
          </Text>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <LineChart data={trendData} margin={{ top: 8, right: 24, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                {chartSeries.map((s) => (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    stroke={s.color}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      <Card className="mt-4 p-0 overflow-auto">
        <Table.Root>
          <Table.Header>
            <Table.Row>
              {columns.map((col) => (
                <Table.ColumnHeaderCell key={col}>
                  {headerFor(col)}
                </Table.ColumnHeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {loading && (
              <Table.Row>
                <Table.Cell colSpan={columns.length || 1}>
                  <Text size="2" color="gray">
                    Loading…
                  </Text>
                </Table.Cell>
              </Table.Row>
            )}
            {!loading && records.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={columns.length || 1}>
                  <Text size="2" color="gray">
                    No data for this period.
                  </Text>
                </Table.Cell>
              </Table.Row>
            )}
            {!loading &&
              records.map((row, idx) => (
                <Table.Row key={`${row.client}-${idx}`}>
                  {columns.map((col) => (
                    <Table.Cell key={col}>{renderCell(row[col])}</Table.Cell>
                  ))}
                </Table.Row>
              ))}
          </Table.Body>
        </Table.Root>
      </Card>
    </AdminLayout>
  );
};

export default AdminAdoptionTrackingPage;
